import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Stack,
} from '@mui/material';
import * as React from 'react';
import { NumericFormat } from 'react-number-format';

import colors from '../../constants/colors';
import PaymentStatusChip from '../../pages/Payment/components/ReviewCardCommonEntry/PaymentStatusChip';

export interface Data {
  policyType: string;
  amount: string;
  policyNumber: string;
}

type DataKeys = 'policyType' | 'policyNumber' | 'amount' | 'confirmation';

interface HeadCell {
  disablePadding: boolean;
  id: DataKeys;
  label: string;
  align: 'left' | 'right';
}

let headCells: HeadCell[];

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: colors.csaaNeutralBackground,
    color: colors.csaaBlack,
    fontFamily: 'Cabin',
    fontKerning: 'normal',
    fontSize: 16,
    fontWeight: 500,
  },
}));

const PaymentsTableHead = ({ showConfirmation = false }) => {
  if (showConfirmation) {
    headCells = [
      {
        id: 'policyType',
        disablePadding: true,
        label: 'Policy type',
        align: 'left',
      },
      {
        id: 'policyNumber',
        disablePadding: false,
        label: 'Policy number',
        align: 'left',
      },
      {
        id: 'confirmation',
        disablePadding: false,
        label: 'Confirmation',
        align: 'left',
      },
      {
        id: 'amount',
        disablePadding: false,
        label: 'Payment amount',
        align: 'right',
      },
    ];
  } else {
    headCells = [
      {
        id: 'policyType',
        disablePadding: true,
        label: 'Policy type',
        align: 'left',
      },
      {
        id: 'policyNumber',
        disablePadding: false,
        label: 'Policy number',
        align: 'left',
      },
      {
        id: 'amount',
        disablePadding: false,
        label: 'Payment amount',
        align: 'right',
      },
    ];
  }

  return (
    <TableHead data-testid="paymentsTableHead">
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell key={headCell.id} align={headCell.align}>
            {headCell.label}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

type Row = {
  policyType: string;
  policyNumber: string;
  policyStatus: string;
  amount: string;
  confirmation?: string;
  failed?: boolean;
};

interface PaymentsTableProps {
  rows: Row[];
  showConfirmation?: boolean;
}

const PaymentsTable = ({
  rows,
  showConfirmation = false,
}: PaymentsTableProps) => (
  <Box data-testid="paymentsTable">
    <Paper elevation={0} sx={{ mb: 2 }}>
      <Typography
        sx={{
          mb: 3,
          fontFamily: 'Cabin',
          fontSize: 18,
          fontWeight: 700,
          lineHeight: '24px',
          display: { mobile: 'none', desktop: 'block' },
        }}
      >
        Pay or edit the amount(s) below.
      </Typography>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size="medium">
          <PaymentsTableHead showConfirmation={showConfirmation} />
          <TableBody>
            {rows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow key={`${row.policyType}-${index}`}>
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    align="left"
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: 16,
                      fontWeight: { mobile: 700, tablet: 400 },
                    }}
                  >
                    {row.policyType}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: 16,
                      fontWeight: { mobile: 700, tablet: 400 },
                    }}
                  >
                    {row.policyNumber}
                  </TableCell>
                  {showConfirmation && (
                    <TableCell
                      align="left"
                      sx={{
                        fontFamily: 'Cabin',
                        fontKerning: 'normal',
                        fontSize: 16,
                        fontWeight: { mobile: 700, tablet: 400 },
                      }}
                    >
                      {row.confirmation}
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'Cabin',
                      fontKerning: 'normal',
                      fontSize: 16,
                      fontWeight: { mobile: 700, tablet: 400 },
                    }}
                  >
                    {showConfirmation && (
                      <Stack
                        spacing={2}
                        justifyContent="flex-end"
                        direction="row"
                      >
                        <Stack alignItems="flex-end">
                          <PaymentStatusChip failed={!!row.failed} />
                        </Stack>
                        <NumericFormat
                          value={row.amount}
                          displayType="text"
                          thousandSeparator
                          prefix="$"
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </Stack>
                    )}
                    {!showConfirmation && (
                      <NumericFormat
                        value={row.amount}
                        displayType="text"
                        thousandSeparator
                        prefix="$"
                        fixedDecimalScale
                        decimalScale={2}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Box>
);

export default PaymentsTable;
